'use client';

import { useState } from 'react';
import { Heart, Share2, MapPin, Tag, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { useLocalizedField } from '@/hooks/useLocalizedField';

interface ActivityCardProps {
  item: {
    id?: string;
    title: string;
    title_en?: string;
    description?: string;
    description_en?: string;
    cover?: string;
    subtype?: string;
    subtype_en?: string;
    address?: string;
    address_en?: string;
    slug?: string;
    [key: string]: any;
  };
}

const ActivityCard = ({ item }: ActivityCardProps) => {
  const locale = useLocale();
  const t = useTranslations();
  const { getLocalizedField } = useLocalizedField();
  const [isHovered, setIsHovered] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [imageError, setImageError] = useState(false);

  const fallbackImage =
    'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?auto=format&fit=crop&w=800&q=80';

  const displayImage = imageError || !item.cover ? fallbackImage : item.cover;

  const localizedDescription = getLocalizedField(item, 'description');
  const truncatedDescription = localizedDescription
    ? localizedDescription.substring(0, 100) +
      (localizedDescription.length > 100 ? '...' : '')
    : t('activities.noDescriptionAvailable');

  const handleLike = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLiked(!isLiked);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: getLocalizedField(item, 'title'),
        text: getLocalizedField(item, 'description') || getLocalizedField(item, 'title'),
        url: window.location.href,
      });
    }
  };

  return (
    <Link href={`/activities/${item.slug || item.id}`} className="block">
      <div
        className="
          relative bg-primary/20 rounded-2xl overflow-hidden
          border border-gray-200 shadow-sm hover:shadow-xl
          transition-all duration-500 h-[400px] w-full group
        "
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        dir={locale === 'ar' ? 'rtl' : 'ltr'}
      >
        {/* Image */}
        <div className="relative h-full w-full overflow-hidden">
          <img
            src={displayImage}
            alt={getLocalizedField(item, 'title')}
            className={`
              w-full h-full object-cover transition-all duration-700
              ${isHovered ? 'scale-110 brightness-90' : 'scale-100 brightness-100'}
            `}
            onError={() => setImageError(true)}
          />

          {/* Black Gradient */}
          <div
            className={`
              absolute inset-0 bg-gradient-to-t from-black/40 via-transparent 
              to-transparent transition-opacity duration-500
              ${isHovered ? 'opacity-100' : 'opacity-80'}
            `}
          />

          {/* Title */}
          <div
            className={`
              absolute top-4 ${locale === 'ar' ? 'right-4 left-4' : 'left-4 right-4'} transition-all duration-500
            `}
          >
            <h3
              className="
                text-white text-xl font-bold drop-shadow-lg line-clamp-2
              "
            >
              {getLocalizedField(item, 'title')}
            </h3>
          </div>

          {/* Address */}
          {getLocalizedField(item, 'address') && (
            <div
              className={`
              absolute bottom-4 ${locale === 'ar' ? 'right-4' : 'left-4'} transition-all duration-500
              group-hover:-translate-y-12
            `}
            >
              <div
                className="
                  bg-black/60 backdrop-blur-sm text-white px-3 py-1.5
                  rounded-full text-sm flex items-center gap-2
                  group-hover:bg-black/80 transition-all duration-300
                "
              >
                <MapPin className="w-3.5 h-3.5" />
                <span>{getLocalizedField(item, 'address')}</span>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div
            className={`
            absolute top-4 ${locale === 'ar' ? 'left-4' : 'right-4'} flex gap-2
            transition-all duration-500 group-hover:opacity-100
          `}
          >
            <button
              onClick={handleLike}
              className={`
                p-2.5 rounded-full backdrop-blur-sm transition-all duration-300
                ${isLiked
                  ? 'bg-red-500/90 text-white scale-110'
                  : 'bg-black/40 text-white hover:bg-black/60 hover:scale-105'
                }
              `}
            >
              <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
            </button>

            <button
              onClick={handleShare}
              className="
              p-2.5 rounded-full bg-black/40 backdrop-blur-sm text-white
              hover:bg-black/60 transition-all duration-300 hover:scale-105
            "
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content Panel */}
        <div
          className={`
            absolute bottom-0 left-0 right-0 bg-gradient-to-t
            from-primary/30 via-primary/20 to-transparent backdrop-blur-sm
            transform transition-all duration-700
            ${isHovered ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}
          `}
        >
          <div className="p-5 pt-8">
            {/* Description */}
            <p className="text-white text-sm mb-5 line-clamp-3">
              {truncatedDescription}
            </p>

            {/* Category */}
            <div className="flex items-center gap-3 mb-5">
              <div
                className="
                  p-2 bg-white rounded-lg text-purple-700
                  group-hover:bg-purple-200 transition-all duration-300
                "
              >
                <Tag className="w-4 h-4" />
              </div>
              <span className="text-white text-sm font-medium">
                {getLocalizedField(item, 'subtype')}
              </span>
            </div>

            {/* View Details */}
            <div className={`pt-4 border-t border-white/30 flex ${locale === 'ar' ? 'justify-start' : 'justify-end'}`}>
              <span
                className="
                text-white text-sm font-semibold flex items-center gap-2
                hover:text-purple-200 transition-colors
              "
              >
                {t('activities.viewDetails')}
                <ChevronRight
                  className={`w-4 h-4 transition-transform ${
                    isHovered ? (locale === 'ar' ? '-translate-x-1 rotate-180' : 'translate-x-1') : (locale === 'ar' ? 'rotate-180' : '')
                  }`}
                />
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ActivityCard;
